<template>
  <div class="fee-combo-member-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeComboMemberManage.page.title')"
      :subtitle="feeComboMemberManageInfo.comboName || $t('feeComboMemberManage.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('feeComboMemberManage.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddFeeComboMemberModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listFeeComboMembers">
        <template #left>
          <span class="ds-panel__title">{{ $t('feeComboMemberManage.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="feeComboMemberManageInfo.feeComboMembers" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('feeComboMemberManage.page.empty')" @retry="_listFeeComboMembers">
        <el-table-column prop="feeTypeCdName" :label="$t('feeComboMemberManage.feeType')" align="left" min-width="120"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeTypeCdName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('feeComboMemberManage.feeItem')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="feeFlagName" :label="$t('feeComboMemberManage.feeFlag')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.feeFlagName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('feeComboMemberManage.paymentType')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.paymentCd == '1200' ? 'info' : 'primary'"
              :label="scope.row.paymentCd == '1200' ? $t('feeComboMemberManage.prepay') : $t('feeComboMemberManage.postpay')" />
          </template>
        </el-table-column>
        <el-table-column prop="paymentCycle" :label="$t('feeComboMemberManage.paymentCycle')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.paymentCycle || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('feeComboMemberManage.unitPrice')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.computingFormula == '2002' ? '-' : formatMoney(scope.row.squarePrice) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('feeComboMemberManage.additionalFee')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.additionalAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteFeeComboMemberModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('feeComboMemberManage.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <add-fee-combo-member ref="addFeeComboMember" @success="handleSuccess" />
    <delete-fee-combo-member ref="deleteFeeComboMember" @success="handleSuccess" />
  </div>
</template>

<script>
import { listFeeComboMember } from '@/api/fee/feeComboMemberManageApi'
import AddFeeComboMember from '@/components/fee/addFeeComboMember'
import DeleteFeeComboMember from '@/components/fee/deleteFeeComboMember'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeComboMemberManageList',
  components: {
    AddFeeComboMember,
    DeleteFeeComboMember,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeComboMemberManageInfo: {
        feeComboMembers: [],
        comboId: '',
        comboName: '',
        conditions: {
          comboId: '',
          comboName: '',
          communityId: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.feeComboMemberManageInfo.conditions.comboId = this.$route.query.comboId
    this.feeComboMemberManageInfo.comboName = this.$route.query.comboName
    this.feeComboMemberManageInfo.conditions.communityId = getCommunityId()
    this._listFeeComboMembers()
  },
  methods: {
    async _listFeeComboMembers() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.feeComboMemberManageInfo.conditions
        }
        const { data, total } = await listFeeComboMember(params)
        this.feeComboMemberManageInfo.feeComboMembers = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取费用套餐成员失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('feeComboMemberManage.page.loadFailed')
        this.$message.error(this.$t('feeComboMemberManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _openAddFeeComboMemberModal() {
      this.$refs.addFeeComboMember.open({
        comboId: this.feeComboMemberManageInfo.conditions.comboId
      })
    },
    _openDeleteFeeComboMemberModel(feeComboMember) {
      this.$refs.deleteFeeComboMember.open(feeComboMember)
    },
    _goBack() {
      this.$router.go(-1)
    },
    handleSuccess() {
      this._listFeeComboMembers()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listFeeComboMembers()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listFeeComboMembers()
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-combo-member-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
